import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/utils';
import { STATUS_LABELS } from '@/lib/company-qualification';
import {
  type QualificationItem,
  type QualificationStatus,
  QUALIFICATION_STATUSES,
} from '@shared/types/company-qualification';

const STATUS_TONES: Record<QualificationStatus, string> = {
  validated: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  observed: 'bg-sky-50 text-sky-800 ring-sky-200',
  hypothesis: 'bg-amber-50 text-amber-800 ring-amber-200',
  gap: 'bg-stone-100 text-stone-700 ring-stone-300',
};

interface QualificationItemRowProps {
  item: QualificationItem;
  onEdit: (text: string) => void;
  onStatus: (status: QualificationStatus) => void;
  onRemove: () => void;
}

/** One line of the sheet: status, text, source on demand. Editing the text validates it. */
export const QualificationItemRow = ({ item, onEdit, onStatus, onRemove }: QualificationItemRowProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.text);
  const [showSource, setShowSource] = useState(false);

  const commit = () => {
    if (draft.trim() && draft.trim() !== item.text) onEdit(draft);
    setEditing(false);
  };

  return (
    <li className="group flex items-start gap-3 py-2">
      <select
        aria-label="Statut"
        value={item.status}
        onChange={(event) => onStatus(event.target.value as QualificationStatus)}
        className={cn('mt-0.5 shrink-0 cursor-pointer appearance-none rounded-full px-2 py-0.5 text-xs font-medium ring-1', STATUS_TONES[item.status])}
      >
        {QUALIFICATION_STATUSES.map((status) => <option key={status} value={status}>{STATUS_LABELS[status]}</option>)}
      </select>
      <div className="min-w-0 flex-1">
        {editing ? (
          <div>
            <Textarea value={draft} onChange={(event) => setDraft(event.target.value)} rows={3} autoFocus className="text-sm" />
            <div className="mt-1 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setDraft(item.text); setEditing(false); }}>Annuler</Button>
              <Button size="sm" onClick={commit} disabled={!draft.trim()}>Valider</Button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setEditing(true)} className="w-full text-left text-sm text-domino-ink hover:text-domino-aubergine">
            {item.text}
          </button>
        )}
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-domino-inkSoft">
          {item.source ? (
            <button type="button" onClick={() => setShowSource(!showSource)} className="underline-offset-2 hover:underline">
              {item.source.label}
            </button>
          ) : <span>Sans source</span>}
          <span>· {item.date}</span>
          {item.destinations?.length ? <span>· {item.destinations.join(', ')}</span> : null}
        </div>
        {showSource && item.source ? (
          <div className="mt-1 rounded-lg bg-domino-muted px-3 py-2 text-xs text-domino-inkSoft">
            {item.source.ref ? (
              /^https?:\/\//.test(item.source.ref)
                ? <a href={item.source.ref} target="_blank" rel="noreferrer" className="break-all underline">{item.source.ref}</a>
                : <p className="break-all">{item.source.ref}</p>
            ) : null}
            {item.source.excerpt ? <p className="mt-1 italic">« {item.source.excerpt} »</p> : null}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Supprimer la ligne"
        onClick={onRemove}
        className="mt-0.5 rounded p-1 text-domino-inkSoft opacity-0 hover:bg-domino-muted group-hover:opacity-100 focus:opacity-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </li>
  );
};
